import EventRepeatOutlined from '@mui/icons-material/EventRepeatOutlined';
import { AlertColor, Box, MenuItem, TextField, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useMemo, useRef, useState } from 'react';
import strings from '../../../../common/Translation/Translate';
import DateInput, { toDateInputString } from '../../../../components/DateInput/DateInput';
import { FormDialog } from '../../../../components/Dialog/FormDialog';
import Notification from '../../../../components/Notification';
import axiosInstance from '../../../../utils/axios';
import { identifiers } from '../../../../utils/constants/identifiers';
import { URLS } from '../../../../utils/constants/urls';

type MoveBookingButtonProps = {
  id: number;
  currentStartDate?: string | null;
  onSuccess: () => void;
  compact?: boolean;
  onTriggerClick?: () => void;
};

const maxReasonLength = 500;

export const MoveBookingButton: React.FC<MoveBookingButtonProps> = ({
  id,
  currentStartDate,
  onSuccess,
  compact,
  onTriggerClick,
}) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  /** Sync guard so a second Save click in the same tick cannot start another PUT. */
  const savingRef = useRef(false);
  const [newStartDate, setNewStartDate] = useState('');
  const [updateReason, setUpdateReason] = useState('');
  const [message, setMessage] = useState<{
    display: boolean;
    severity: AlertColor | null;
    message: string;
  }>({
    display: false,
    severity: null,
    message: '',
  });

  const today = useMemo(() => dayjs().startOf('day'), []);
  const currentDateLabel = currentStartDate ? dayjs(currentStartDate).format('D MMM YYYY') : '—';

  const handleOpen = () => {
    onTriggerClick?.();
    setNewStartDate('');
    setUpdateReason('');
    setOpenDialog(true);
  };

  const handleClose = () => {
    setOpenDialog(false);
    setNewStartDate('');
    setUpdateReason('');
  };

  const handleSave = async () => {
    if (savingRef.current || isLoading) return;
    const dateOnly = toDateInputString(newStartDate);
    if (!dateOnly) {
      setMessage({ display: true, severity: 'warning', message: strings.requiredText });
      return;
    }
    const selectedDay = dayjs(dateOnly, identifiers.date_input);
    if (selectedDay.isBefore(today, 'day')) {
      setMessage({ display: true, severity: 'warning', message: strings.move_booking_past_date_error });
      return;
    }
    if (currentStartDate && selectedDay.isSame(dayjs(currentStartDate), 'day')) {
      setMessage({ display: true, severity: 'warning', message: strings.move_booking_same_date_error });
      return;
    }
    if (updateReason.length > maxReasonLength) {
      setMessage({
        display: true,
        severity: 'warning',
        message: strings.formatString(strings.exceeded_character_limit, maxReasonLength) as string,
      });
      return;
    }

    savingRef.current = true;
    try {
      setIsLoading(true);
      await axiosInstance.put(URLS.moveBookingLineItem(id), {
        newStartDate: dateOnly,
        updateReason: updateReason.trim() || undefined,
      });
      setMessage({ display: true, severity: 'success', message: strings.move_booking_success });
      handleClose();
      onSuccess();
    } catch (error: any) {
      setMessage({
        display: true,
        severity: 'error',
        message: error.response?.data?.error_description || error.response?.data?.message || error.message,
      });
    } finally {
      savingRef.current = false;
      setIsLoading(false);
    }
  };

  return (
    <>
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={() => setMessage({ ...message, display: false })}
          autoHideDuration={4000}
        />
      )}

      {compact ? (
        <MenuItem onClick={handleOpen} sx={{ py: 0.75, gap: 1, fontSize: '0.875rem', fontWeight: 500 }}>
          <EventRepeatOutlined sx={{ fontSize: 18, color: 'text.secondary' }} />
          {strings.move_booking_menu_label}
        </MenuItem>
      ) : null}

      <FormDialog
        open={openDialog}
        handleClose={handleClose}
        handleSave={handleSave}
        loading={isLoading}
        title={strings.move_booking_dialog_title}
        contentText={strings.move_booking_dialog_subtitle}
        saveButtonText={strings.move_booking_confirm_button}
        disabledCondition={!newStartDate}
        maxWidth="sm">
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {strings.formatString(strings.move_booking_current_date_label, currentDateLabel) as string}
          </Typography>
          <Box sx={{ '& .MuiFormControl-root, & .MuiTextField-root': { width: '100%' } }}>
            <DateInput
              field="newStartDate"
              label={strings.move_booking_new_date_label}
              inputFormat={identifiers.date_format}
              value={newStartDate ? dayjs(newStartDate, identifiers.date_input) : null}
              minDate={today}
              textChange={(val: string) => setNewStartDate(toDateInputString(val))}
              onChange={() => {
                /* handled by textChange */
              }}
              renderInput={() => <TextField size="medium" fullWidth />}
              validators={[]}
            />
          </Box>
          <TextField
            label={strings.move_booking_reason_label}
            value={updateReason}
            onChange={e => setUpdateReason(e.target.value)}
            multiline
            minRows={2}
            maxRows={4}
            fullWidth
            helperText={strings.move_booking_reason_optional}
          />
        </Box>
      </FormDialog>
    </>
  );
};
